import { useQuery, useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import React from 'react';
import { MenuOption, MenuOptionGroup } from '../../types';
import { NobleModal } from '../../../../components';
import { getOptionsMinusExisting } from '../utils';

import { AvailableOptions, AvailableOptionsVariables } from './__generated__/AvailableOptions';
import { RequestCuratedOption, RequestCuratedOptionVariables } from './__generated__/RequestCuratedOption';

import { OptionSelectEditor } from './OptionSelectEditor';
import { OptionFreeformEditor } from './OptionFreeformEditor';

interface AddOptionModalContentProps {
  currentOptions: MenuOption[];
  name: string;
  allowCustom: boolean;
  itemName?: string;
  onCancel();
  onSubmit(options: MenuOption[]);

  venueId: string;
  menuId: string;
  categoryId: string;
  itemId?: string;
  optionGroupId: string;
}

const OPTIONS_QUERY = gql`
  query AvailableOptions($venueId: ID!, $menuId: ID!, $categoryId: ID!, $optionGroupId: ID!, $itemId: ID) {
    optionsForType(
      venueId: $venueId
      menuId: $menuId
      categoryId: $categoryId
      optionGroupId: $optionGroupId
      itemId: $itemId
    ) {
      id
      name
      imageUrl
      posInfo {
        externalId
        overridedFields
      }
      posPrice
    }
  }
`;

const REQUEST_OPTION = gql`
  mutation RequestCuratedOption($venueId: ID!, $optionName: String!, $itemName: String!) {
    requestABrand(venueId: $venueId, brandName: $optionName, drinkName: $itemName)
  }
`;

export const AddOptionModalContent: React.FC<AddOptionModalContentProps> = (props) => {
  const { currentOptions, itemName, venueId } = props;
  const { data, loading } = useQuery<AvailableOptions, AvailableOptionsVariables>(OPTIONS_QUERY, {
    variables: {
      venueId,
      menuId: props.menuId,
      categoryId: props.categoryId,
      optionGroupId: props.optionGroupId,
      itemId: props.itemId,
    },
  });

  const options = React.useMemo(() => {
    return getOptionsMinusExisting(data?.optionsForType, currentOptions);
  }, [currentOptions, data]);

  const [requestOption, requestOptionState] = useMutation<RequestCuratedOption, RequestCuratedOptionVariables>(
    REQUEST_OPTION,
    {
      onCompleted() {
        props.onCancel();
      },
    }
  );
  const onRequest = React.useCallback(
    (optionName: string) => {
      return requestOption({
        variables: {
          itemName: itemName ?? '',
          optionName,
          venueId,
        },
      });
    },
    [itemName, venueId, requestOption]
  );

  if (requestOptionState.loading) {
    return <NobleModal.Body>Sending request...</NobleModal.Body>;
  }

  if (loading || !data) {
    return <NobleModal.Body>Loading...</NobleModal.Body>;
  }

  if (data.optionsForType.length === 0 && props.allowCustom) {
    return <OptionFreeformEditor name={props.name} onCancel={props.onCancel} onSubmit={props.onSubmit} />;
  }

  return (
    <OptionSelectEditor
      name={props.name}
      allowCustom={props.allowCustom}
      options={options}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onRequest={onRequest}
    />
  );
};

interface AddOptionModalProps {
  optionGroup: MenuOptionGroup;
  isOpen: boolean;

  itemName?: string;
  onCancel();
  onSubmit(options: MenuOption[]);
  isPOSItem: boolean;

  venueId: string;
  menuId: string;
  categoryId: string;
  itemId?: string;
}

export const AddOptionModal: React.FC<AddOptionModalProps> = (props) => {
  const { optionGroup, isPOSItem } = props;
  const { editorConfig: config } = optionGroup;

  return (
    <NobleModal isOpen={props.isOpen} toggle={props.onCancel}>
      <NobleModal.Title title={`Add ${optionGroup.name}`} />
      <AddOptionModalContent
        currentOptions={optionGroup.menuOptions}
        itemName={props.itemName}
        onCancel={props.onCancel}
        onSubmit={props.onSubmit}
        venueId={props.venueId}
        menuId={props.menuId}
        categoryId={props.categoryId}
        optionGroupId={props.optionGroup.id}
        itemId={props.itemId}
        name={optionGroup.name}
        allowCustom={isPOSItem ? false : config.allowCustom || config.widget === 'freeform-list'}
      />
    </NobleModal>
  );
};
